import { useEffect, useState } from "react";
import {
  REENGAGEMENT_LIMITS,
  type ReengagementSettings as Settings,
  type UpdateReengagementSettingsInput,
} from "@school-sales-ai/shared";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { delayToMinutes, splitDelay, type DelayUnit } from "@/lib/reengagement-delay";
import {
  useReengagementSettings,
  useUpdateReengagementSettings,
} from "@/hooks/useReengagement";

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("es-MX", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

function hourLabel(hour: number): string {
  return hour === 24 ? "24:00" : `${String(hour).padStart(2, "0")}:00`;
}

const HOURS_FROM = Array.from({ length: 24 }, (_, h) => h);
const HOURS_UNTIL = Array.from({ length: 24 }, (_, h) => h + 1);
const MESSAGE_COUNTS = Array.from(
  { length: REENGAGEMENT_LIMITS.maxMessages - REENGAGEMENT_LIMITS.minMessages + 1 },
  (_, i) => REENGAGEMENT_LIMITS.minMessages + i,
);

function SettingsBody({ settings }: { settings: Settings }): React.JSX.Element {
  const update = useUpdateReengagementSettings();
  const disabled = !settings.canManage || update.isPending;
  const save = (patch: UpdateReengagementSettingsInput): void => update.mutate(patch);

  // The delay is typed, so it is a draft until it is valid and leaves the field;
  // every other control saves the moment it changes.
  const initial = splitDelay(settings.delayMinutes);
  const [amount, setAmount] = useState(String(initial.amount));
  const [unit, setUnit] = useState<DelayUnit>(initial.unit);
  useEffect(() => {
    const next = splitDelay(settings.delayMinutes);
    setAmount(String(next.amount));
    setUnit(next.unit);
  }, [settings.delayMinutes]);
  const draftMinutes = delayToMinutes(amount, unit);
  const commitDelay = (minutes: number | null): void => {
    if (minutes !== null && minutes !== settings.delayMinutes) save({ delayMinutes: minutes });
  };

  return (
    <div className="border-t border-ink-200 pt-4 mt-4">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <label htmlFor="reengagement-enabled" className="text-[13px] font-medium text-ink-900">
            Mensajes de seguimiento
          </label>
          <p className="text-[12px] text-ink-500 mt-0.5">
            Cuando el agente queda esperando la respuesta de un cliente en WhatsApp y este deja
            de contestar, el agente le vuelve a escribir para retomar lo que quedó pendiente. No
            escribe si la conversación ya terminó, y deja de hacerlo si el cliente lo pide.
          </p>
        </div>
        <Switch
          id="reengagement-enabled"
          checked={settings.enabled}
          disabled={disabled}
          onCheckedChange={(checked) => save({ enabled: checked })}
          aria-label="Enviar mensajes de seguimiento"
        />
      </div>

      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <label htmlFor="reengagement-delay" className="text-[12.5px] font-medium text-ink-700">
            Esperar antes de cada seguimiento
          </label>
          <div className="flex gap-2">
            <Input
              id="reengagement-delay"
              inputMode="numeric"
              value={amount}
              disabled={disabled}
              onChange={(e) => setAmount(e.target.value)}
              onBlur={() => commitDelay(draftMinutes)}
              aria-invalid={draftMinutes === null}
              className={`w-24 ${draftMinutes === null ? "border-red-400" : ""}`}
            />
            <Select
              value={unit}
              disabled={disabled}
              onValueChange={(v) => {
                const next: DelayUnit = v === "hours" ? "hours" : "minutes";
                setUnit(next);
                commitDelay(delayToMinutes(amount, next));
              }}
            >
              <SelectTrigger className="w-32" aria-label="Unidad">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="minutes">minutos</SelectItem>
                <SelectItem value="hours">horas</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {draftMinutes === null ? (
            <p className="text-[11px] text-red-500">
              Elige entre {REENGAGEMENT_LIMITS.minDelayMinutes} minutos y{" "}
              {REENGAGEMENT_LIMITS.maxDelayMinutes / 60} horas.
            </p>
          ) : (
            <p className="text-[11px] text-ink-500">
              Se cuenta desde el último mensaje del agente.
            </p>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="text-[12.5px] font-medium text-ink-700">
            Número máximo de seguimientos
          </label>
          <Select
            value={String(settings.maxMessages)}
            disabled={disabled}
            onValueChange={(v) => save({ maxMessages: Number(v) })}
          >
            <SelectTrigger className="w-32" aria-label="Número máximo de seguimientos">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {MESSAGE_COUNTS.map((n) => (
                <SelectItem key={n} value={String(n)}>
                  {n}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-[11px] text-ink-500">
            Por cada vez que el cliente deja de contestar. Si responde, la cuenta empieza de nuevo.
          </p>
        </div>

        <div className="flex flex-col gap-1.5 sm:col-span-2">
          <label className="text-[12.5px] font-medium text-ink-700">Horario de envío</label>
          <div className="flex items-center gap-2">
            <Select
              value={String(settings.sendFromHour)}
              disabled={disabled}
              onValueChange={(v) => save({ sendFromHour: Number(v) })}
            >
              <SelectTrigger className="w-28" aria-label="Desde">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {HOURS_FROM.map((h) => (
                  <SelectItem key={h} value={String(h)} disabled={h >= settings.sendUntilHour}>
                    {hourLabel(h)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="text-[12.5px] text-ink-500">a</span>
            <Select
              value={String(settings.sendUntilHour)}
              disabled={disabled}
              onValueChange={(v) => save({ sendUntilHour: Number(v) })}
            >
              <SelectTrigger className="w-28" aria-label="Hasta">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {HOURS_UNTIL.map((h) => (
                  <SelectItem key={h} value={String(h)} disabled={h <= settings.sendFromHour}>
                    {hourLabel(h)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <p className="text-[11px] text-ink-500">
            Fuera de este horario no se envía nada; lo pendiente sale cuando vuelve a abrir.
          </p>
        </div>
      </div>

      {settings.hasReplyWindow ? (
        <p className="mt-3 rounded-md bg-amber-50 px-3 py-2 text-[12px] text-amber-800">
          Con la API oficial de WhatsApp solo se puede escribir dentro de las 24 horas siguientes
          al último mensaje del cliente. Un seguimiento que caiga después de ese plazo no se
          envía.
        </p>
      ) : null}

      {settings.updatedAt ? (
        <p className="mt-3 text-xs text-ink-500">
          Último cambio
          {settings.updatedByName ? ` por ${settings.updatedByName}` : ""} el{" "}
          {formatDate(settings.updatedAt)}.
        </p>
      ) : null}
      {!settings.canManage ? (
        <p className="mt-1 text-xs text-ink-500">
          Solo un administrador puede cambiar estos ajustes.
        </p>
      ) : null}
    </div>
  );
}

/**
 * Agente → Ajustes → «Mensajes de seguimiento».
 *
 * Saves itself, outside the tab's Guardar cycle (the `LeadAssignmentSettings`
 * pattern): these decide when the agent writes to real customers, and only an
 * admin may change them — the shared Ajustes form is open to members. Rendered
 * read-only for everyone else, disabled by the server's `canManage`.
 */
export function ReengagementSettings(): React.JSX.Element | null {
  const { data: settings, isPending } = useReengagementSettings();
  if (isPending || !settings) return null;
  return <SettingsBody settings={settings} />;
}
